import { useEffect, useState, useMemo } from "react";
import BarChart from "./BarChart";
import {
  CHART_BG_COLORS,
  CHART_BORDER_COLORS,
  check_date_within_range,
} from "../../utils/reports";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import { CameraItem } from "../../models/Camera";

interface SpeciesByMonthsProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  startDate: string;
  endDate: string;
}

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const useProcessedReportData = (
  reportData: IdentifiedSpeciesCount[],
  startDate: string,
  endDate: string
) => {
  return useMemo(() => {
    const map = new Map();
    reportData.forEach((data) => {
      if (!check_date_within_range(startDate, endDate, data.date)) return;
      const key = `${data.identified_object_name}-${data.camera}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push({ date: data.date, video_count: 1 });
    });
    return map;
  }, [reportData, startDate, endDate]);
};

export const getCount = (
  objectName: string,
  monthIndex: number,
  cameraId: string,
  dataMap: Map<string, any[]>
) => {
  const key = `${objectName}-${cameraId}`;
  const matchingData = dataMap.get(key) || [];
  return matchingData.reduce((total, data) => {
    const dataMonth = new Date(data.date).getUTCMonth();
    return dataMonth === monthIndex ? total + 1 : total;
  }, 0);
};

export const getTotalCount = (
  objectName: string,
  monthIndex: number,
  selectedCameras: CameraItem[],
  dataMap: Map<string, any[]>
) => {
  return selectedCameras.reduce(
    (total, camera) =>
      total + getCount(objectName, monthIndex, camera.camera_id, dataMap),
    0
  );
};

const SpeciesByMonths = ({
  cameras,
  selectedCameras,
  allIdentifiedObjects,
  selectedIdentifiedObjects,
  reportData,
  startDate,
  endDate,
}: SpeciesByMonthsProps) => {
  const [mapData, setMapData] = useState<any>(null);
  const [csvData, setCsvData] = useState<any>();

  const dataMap = useProcessedReportData(reportData, startDate, endDate);
  const ChartName = "Species Observed by Month";

  useEffect(() => {
    const csv = selectedCameras.flatMap((camera) =>
      selectedIdentifiedObjects.flatMap((object) =>
        monthNames.map((_, monthIndex) => ({
          camera: camera.camera_id,
          Object: object.name,
          month: monthIndex + 1,
          Count: getCount(object.name, monthIndex, camera.camera_id, dataMap),
        }))
      )
    );
    setCsvData(csv);
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  useEffect(() => {
    if (!selectedCameras.length || !selectedIdentifiedObjects.length) return;

    const datasets = selectedIdentifiedObjects.map((object, index) => ({
      label: object.name,
      data: monthNames.map((_, monthIndex) =>
        getTotalCount(object.name, monthIndex, selectedCameras, dataMap)
      ),
      backgroundColor:
        CHART_BG_COLORS[
          Object.keys(CHART_BG_COLORS)[
            index % Object.keys(CHART_BG_COLORS).length
          ]
        ],
      borderColor:
        CHART_BORDER_COLORS[
          Object.keys(CHART_BORDER_COLORS)[
            index % Object.keys(CHART_BORDER_COLORS).length
          ]
        ],
      borderWidth: 1,
    }));

    setMapData({ labels: monthNames, datasets });
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  const getYAxisConfig = (datasets: any[]) => {
    const allValues = datasets?.flatMap((dataset) => dataset.data) || [];
    const maxValue = Math.max(10, ...allValues);
    const stepSize = maxValue < 10 ? 1 : maxValue < 100 ? 10 : 50;
    return {
      suggestedMax: Math.ceil(maxValue / stepSize) * stepSize,
      stepSize,
    };
  };

  const { suggestedMax, stepSize } = mapData
    ? getYAxisConfig(mapData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Animals Observed by Month" },
    },
    layout: { padding: { left: 0, right: 10, top: 0, bottom: 0 } },
    scales: {
      y: { min: 0, suggestedMax, ticks: { stepSize } },
    },
  };

  return (
    <BarChart
      mapData={mapData}
      options={options}
      csvData={csvData}
      name={ChartName}
    />
  );
};

export default SpeciesByMonths;
